/** Mirrors backend CommonConfig.OFFLINE_SUBSCRIPTION_USER_STATUS */
export const OfflineSubscriptionUserStatus = {
  NONE: 0,
  PENDING_VERIFY: 1,
  ACTIVE: 2,
  REJECTED: 3,
  EXPIRED: 4,
  SUSPENDED: 5,
} as const;

function isSalonOwnerApproved(user: any): boolean {
  if (!user) return false;
  return Number(user.role_id) === 1 && Number(user.is_approved) === 1;
}

/** Owner with admin-suspended subscription — same route/nav rules as expired (gated modules only). */
export function salonOwnerSubscriptionSuspended(user: any): boolean {
  if (!isSalonOwnerApproved(user)) return false;
  return (
    Number(user.offline_subscription_status) ===
    OfflineSubscriptionUserStatus.SUSPENDED
  );
}

/** Expired-by-date/status or admin-suspended: may use dashboard etc., but not subscription-gated modules. */
export function salonOwnerSubscriptionFeatureLocked(user: any): boolean {
  return (
    salonOwnerSubscriptionExpired(user) ||
    salonOwnerSubscriptionSuspended(user)
  );
}

/**
 * Owner’s plan has ended (status EXPIRED, or ACTIVE with past expires_at).
 * Not suspended; not staff. Used to lock only subscription-gated features (services, staff, offline booking).
 */
export function salonOwnerSubscriptionExpired(user: any): boolean {
  if (!isSalonOwnerApproved(user)) return false;
  const st = Number(user.offline_subscription_status);
  if (st === OfflineSubscriptionUserStatus.SUSPENDED) return false;
  if (st === OfflineSubscriptionUserStatus.EXPIRED) return true;
  const exp = user.offline_subscription_expires_at;
  if (exp && new Date(exp).getTime() < Date.now()) return true;
  return false;
}

/**
 * Owner still needs to complete / fix subscription (pending, rejected, never active, etc.).
 * Expired owners return false here so they can use the app except subscription-gated modules.
 */
export function salonOwnerNeedsSubscription(user: any): boolean {
  if (!isSalonOwnerApproved(user)) return false;
  const st = Number(user.offline_subscription_status);
  if (st === OfflineSubscriptionUserStatus.SUSPENDED) return false;
  if (salonOwnerSubscriptionExpired(user)) return false;
  if (st !== OfflineSubscriptionUserStatus.ACTIVE) return true;
  const exp = user.offline_subscription_expires_at;
  if (exp && new Date(exp).getTime() < Date.now()) return true;
  return false;
}

/** First path segment (same usage as PrivateRoute) for owners with expired subscription. */
export const SUBSCRIPTION_EXPIRED_LOCKED_PATH_SEGMENTS = [
  'service-management',
  'service',
  'service-update',
  'staff-member',
  'offline-booking',
];

/** When subscription is missing/expired/etc. — dashboard + subscription + profile setup still allowed. */
export const SUBSCRIPTION_EXEMPT_PATH_SEGMENTS = [
  "dashboard",
  "subscription-plans",
  "subscription-purchase",
  "profile",
  "complete-profile",
];

